"use client";

import type { ComponentProps, ReactNode } from "react";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { useIsMobile } from "../lib/hooks/use-mobile";
import { BottomSheetDrawer } from "./bottom-sheet-drawer";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

export type ResponsiveDropdownMenuItem = {
  key: string;
  label: ReactNode;
  icon?: ReactNode;
  onSelect?: () => void;
  disabled?: boolean;
  className?: string;
};

export type ResponsiveDropdownMenuProps = {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  trigger: ReactNode;
  items: ReadonlyArray<ResponsiveDropdownMenuItem>;
  modal?: boolean;
  align?: ComponentProps<typeof DropdownMenuContent>["align"];
  sideOffset?: number;
  drawerTitle?: ReactNode;
  drawerDescription?: ReactNode;
  closeLabel?: string;
  showClose?: boolean;
  dismissible?: boolean;
  itemClassName?: string;
  dropdownContentClassName?: string;
  dropdownItemClassName?: string;
  drawerClassName?: string;
  drawerContentClassName?: string;
  drawerItemClassName?: string;
};

export const ResponsiveDropdownMenu = ({
  open: externalOpen,
  onOpenChange: externalOnOpenChange,
  trigger,
  items,
  modal = true,
  align = "center",
  sideOffset = 4,
  drawerTitle,
  drawerDescription,
  closeLabel = "Close",
  showClose = true,
  dismissible = true,
  itemClassName,
  dropdownContentClassName,
  dropdownItemClassName,
  drawerClassName,
  drawerContentClassName,
  drawerItemClassName,
}: ResponsiveDropdownMenuProps) => {
  const isMobile = useIsMobile();
  const [internalOpen, setInternalOpen] = useState(false);

  const isControlled = externalOpen !== undefined;
  const open = isControlled ? externalOpen : internalOpen;

  const handleOpenChange = (newOpen: boolean) => {
    externalOnOpenChange?.(newOpen);

    if (!isControlled) {
      setInternalOpen(newOpen);
    }
  };

  if (isMobile) {
    return (
      <BottomSheetDrawer
        open={open}
        onOpenChange={handleOpenChange}
        trigger={trigger}
        title={drawerTitle}
        description={drawerDescription}
        closeLabel={closeLabel}
        showClose={showClose}
        dismissible={dismissible}
        className={drawerClassName}
        contentClassName={cn("p-2", drawerContentClassName)}
        content={
          <div className="flex flex-col">
            {items.map((item) => (
              <Button
                className={cn(
                  "h-auto justify-start gap-3 px-4 py-4 text-left font-normal text-base",
                  itemClassName,
                  drawerItemClassName,
                  item.className,
                )}
                disabled={item.disabled}
                key={item.key}
                onClick={() => {
                  item.onSelect?.();
                  handleOpenChange(false);
                }}
                type="button"
                variant="ghost"
              >
                {item.icon ? (
                  <span className="shrink-0">{item.icon}</span>
                ) : null}
                <span className="flex-1">{item.label}</span>
              </Button>
            ))}
          </div>
        }
      />
    );
  }

  return (
    <DropdownMenu modal={modal} onOpenChange={handleOpenChange} open={open}>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent
        align={align}
        className={dropdownContentClassName}
        sideOffset={sideOffset}
      >
        {items.map((item) => (
          <DropdownMenuItem
            className={cn(
              "cursor-pointer",
              itemClassName,
              dropdownItemClassName,
              item.className,
            )}
            disabled={item.disabled}
            key={item.key}
            onSelect={() => {
              item.onSelect?.();
            }}
          >
            {item.icon}
            {item.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
